import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid2,
  IconButton,
  Stack,
  Switch,
} from "@mui/material";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialInput from "../../../components/MaterialInput";
import MaterialBox from "../../../components/MaterialBox";
import { useExternalApiRegistryFormState } from "./components/ExternalApiRegistryStateHandle";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import DashboardFooter from "../../../layoutcontainers/DashboardLayout/components/Footer";
import { fetchData, fetchDataById, postData, putData } from "../../../services";
import { useEffect, useState } from "react";
import ConfirmationDialog from "../../../components/Dialog/ConfirmationDialog";
import { useSnackbar } from "../../../context/popup";
import MaterialSelect from "../../../components/MasterSelect";
import DropDownConfig from "../../Utils/DropDownConfig";

const ExternalApiRegistryForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();
  const apiRegistryId = searchParams.get("id");
  const pageType = searchParams.get("pageType");
  const [generalProductOptions, setGeneralProductOptions] = useState([]);
  const [apiStatusOptions, setApiStatusOptions] = useState([]);
  const [registryOptions, setRegistryOptions] = useState([]);

  const { formData, setFormData, handleChange, checkAllFields } =
    useExternalApiRegistryFormState();

  //dropdown apis
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const results = await Promise.allSettled([
          fetchData(`/reference/general/key-value-store?dropdown=true&code=${DropDownConfig?.generalProduct}`), // gProductId

          fetchData(
            `/api-engine/key-value-store?dropdown=true&isActive=true&code=${DropDownConfig?.apiStatus}`
          ), // apiStatus

          fetchData(`/reference/api-engine/insurer-registry?dropdown=true`), // registry
        ]);

        const [productResult, apiStatusResult, registryResult] = results;

        // General Product
        if (
          productResult.status === "fulfilled" &&
          productResult.value?.success
        ) {
          setGeneralProductOptions(
            productResult.value.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }

        // API Status
        if (
          apiStatusResult.status === "fulfilled" &&
          apiStatusResult.value?.success
        ) {
          setApiStatusOptions(
            apiStatusResult.value.data?.items?.map((item) => ({
              value: item.id,
              label: item.keyName,
            })) || []
          );
        }
        // Registry (FIXED: correct response used)
        if (
          registryResult.status === "fulfilled" &&
          registryResult.value?.success
        ) {
          setRegistryOptions(
            registryResult.value.data?.insurer_registries?.map((item) => ({
              value: item.id,
              label: `iRegistry-${item.id}`,
            })) || []
          );
        }

        // Optional warning if any API failed
        if (results.some((r) => r.status === "rejected")) {
          showSnackbar("Some dropdowns failed to load", "warning");
        }
      } catch (error) {
        console.error("Unexpected dropdown error:", error);
        showSnackbar("Error fetching Dropdowns", "error");
      }
    };

    fetchDropdowns();
  }, []);

  const pageMode =
    pageType === "view" ? "view" : pageType === "edit" ? "edit" : "add";

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    showCancel: true,
    showConfirm: true,
    customSection: null,
    onConfirm: () => {},
    onCancel: () => {},
  });

  useEffect(() => {
    if (!apiRegistryId) return;

    const fetchRegistry = async () => {
      const res = await fetchDataById(
        `/api-engine/api-registry/${apiRegistryId}`
      );

      if (res?.success) {
        const d = res.data;

        setFormData((prev) => ({
          ...prev,

          name: d.name || "",
          code: d.code || "",
          description: d.description || "",
          isActive: d.isActive ?? true,

          // ✅ Registry (Insurer)
          iRegistryId: d.insurerDetails
            ? {
                label: d.insurerDetails.id, // or insurer name if available later
                value: d.insurerDetails.id,
              }
            : null,

          // ✅ Product
          gProductId: d.productDetails
            ? {
                label: d.productDetails.keyName,
                value: d.productDetails.id,
              }
            : null,

          // ✅ API Status
          apiStatusId: d.apiStatusDetails
            ? {
                label: d.apiStatusDetails.keyName,
                value: d.apiStatusDetails.id,
              }
            : null,

          nameStatus: "success",
          codeStatus: "success",
          descriptionStatus: "success",
          gProductIdStatus: "success",
          apiStatusIdStatus: "success",
          iRegistryIdStatus: "success",
        }));
      }
    };

    fetchRegistry();
  }, [apiRegistryId]);

  //POST and PUT Action Function
  const handleApiRegistryAction = async () => {
    const payload = {
      name: formData.name,
      code: formData.code,
      description: formData.description,
      iRegistryId: formData.iRegistryId?.value,
      gProductId: formData.gProductId?.value,
      apiStatusId: formData.apiStatusId?.value,
      isActive: formData.isActive,
    };

    //Update
    if (apiRegistryId) {
      try {
        const response = await putData(
          `/api-engine/api-registry/${apiRegistryId}`,
          payload
        );
        const responseMessage = response?.message;
        if (response?.success) {
          //success message
          showSnackbar(responseMessage, "success");
          navigate(-1);
        } else {
          //error message
          showSnackbar(responseMessage, "error");
        }
      } catch (error) {
        console.error("Error updating data:", error);
      }
    } else {
      //Submit
      try {
        const response = await postData("/api-engine/api-registry", payload);
        const responseMessage = response?.message;
        if (response?.success) {
          //success message
          showSnackbar(responseMessage, "success");
          navigate(-1);
        } else {
          //error message
          showSnackbar(responseMessage, "error");
        }
      } catch (error) {
        console.error("Error posting data:", error);
        //catch error message
      }
    }
  };

  return (
    <>
      <DashboardLayout>
        {confirmationDialog.open && (
          <ConfirmationDialog
            open={confirmationDialog.open}
            title={confirmationDialog.title}
            message={confirmationDialog.message}
            showCancel={confirmationDialog.showCancel}
            showConfirm={confirmationDialog.showConfirm}
            customSection={confirmationDialog.customSection}
            onConfirm={confirmationDialog.onConfirm}
            onCancel={confirmationDialog.onCancel}
          />
        )}
        <Card sx={{ minHeight: 500 }}>
          <CardHeader
            title={
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton onClick={() => navigate(-1)}>
                    <ArrowBack />
                  </IconButton>
                  <Stack direction="column" spacing={0}>
                    <MaterialTypography variant="h5" fontWeight="medium">
                     API Registry Details
                    </MaterialTypography>
                    <MaterialTypography variant="caption" color="text">
                      Enter New API Registry Details
                    </MaterialTypography>
                  </Stack>
                </Stack>
              </Stack>
            }
          />

          <CardContent>
            <Grid2 container spacing={1}>
              {/* iRegistryId */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <MaterialTypography variant="caption" fontWeight="bold">
                  Insurer Registry*
                </MaterialTypography>
                <MaterialSelect
                  name="iRegistryId"
                  value={formData.iRegistryId}
                  options={registryOptions}
                  onChange={(opt) => handleChange(opt, { name: "iRegistryId" })}
                  success={formData.iRegistryIdStatus === "success"}
                  error={formData.iRegistryIdStatus === "error"}
                  isDisabled={pageMode === "view"}
                />
              </Grid2>

              {/* Name */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <MaterialTypography variant="caption" fontWeight="bold">
                  API Name*
                </MaterialTypography>
                <MaterialInput
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  success={formData.nameStatus === "success"}
                  error={formData.nameStatus === "error"}
                  disabled={pageMode === "view"}
                />
                {formData.nameStatus === "error" && (
                  <FormControl error>
                      <FormHelperText>
                      Enter API Name of minimum 1 character.
                      </FormHelperText>
                  </FormControl>
                )}
              </Grid2>
              {/* Code */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <MaterialTypography variant="caption" fontWeight="bold">
                  API Code*
                </MaterialTypography>
                <MaterialInput
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  success={formData.codeStatus === "success"}
                  error={formData.codeStatus === "error"}
                  disabled={pageMode === "view"}
                />
                {formData.codeStatus === "error" && (
                  <FormControl error>
                      <FormHelperText>
                      Enter API Code between 1-50 characters.
                      </FormHelperText>
                  </FormControl>
                )}
              </Grid2>

              {/* General Product */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <MaterialTypography variant="caption" fontWeight="bold">
                  General Product*
                </MaterialTypography>
                <MaterialSelect
                  name="gProductId"
                  value={formData.gProductId}
                  options={generalProductOptions}
                  onChange={(opt) => handleChange(opt, { name: "gProductId" })}
                  success={formData.gProductIdStatus === "success"}
                  error={formData.gProductIdStatus === "error"}
                  isDisabled={pageMode === "view"}
                />
              </Grid2>

              {/* API Status */}
              <Grid2 size={{ xs: 12, md: 6 }}>
                <MaterialTypography variant="caption" fontWeight="bold">
                  API Status*
                </MaterialTypography>
                <MaterialSelect
                  name="apiStatusId"
                  value={formData.apiStatusId}
                  options={apiStatusOptions}
                  onChange={(opt) => handleChange(opt, { name: "apiStatusId" })}
                  success={formData.apiStatusIdStatus === "success"}
                  error={formData.apiStatusIdStatus === "error"}
                  isDisabled={pageMode === "view"}
                />
              </Grid2>

              {/* Active */}
              <Grid2 sx={{ pt: 4, pl: 2 }} size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "isActive",
                            value: e.target.checked,
                          },
                        })
                      }
                      disabled={pageMode === "view"}
                    />
                  }
                  label={formData.isActive ? "Active" : "Inactive"}
                />
              </Grid2>

              {/* Description */}
              <Grid2 size={{ xs: 12 }}>
                <MaterialTypography variant="caption" fontWeight="bold">
                  Description
                </MaterialTypography>
                <MaterialInput
                  name="description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  success={formData.descriptionStatus === "success"}
                  error={formData.descriptionStatus === "error"}
                  disabled={pageMode === "view"}
                />
                {formData.descriptionStatus === "error" && (
                    <FormControl error>
                        <FormHelperText>
                        Description should be maximum 500 characters.
                        </FormHelperText>
                    </FormControl>
                )}
              </Grid2>
            </Grid2>
          </CardContent>

          <MaterialBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {pageMode === "add" && (
              <>
                <MaterialButton
                  sx={{ m: 2 }}
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </MaterialButton>
                <MaterialButton
                  sx={{ m: 2 }}
                  variant="gradient"
                  color="dark"
                  disabled={!checkAllFields()}
                  onClick={handleApiRegistryAction}
                >
                  Submit
                </MaterialButton>
              </>
            )}

            {pageMode === "edit" && (
              <>
                <MaterialButton
                  sx={{ m: 2 }}
                  variant="outlined"
                  color="dark"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </MaterialButton>
                <MaterialButton
                  sx={{ m: 2 }}
                  variant="gradient"
                  color="dark"
                  disabled={!checkAllFields()}
                  onClick={handleApiRegistryAction}
                >
                  Update
                </MaterialButton>
              </>
            )}
          </MaterialBox>
        </Card>
      </DashboardLayout>
      <DashboardFooter />
    </>
  );
};

export default ExternalApiRegistryForm;
